import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map } from 'rxjs';
import { baseUrl } from '../../constants';

@Injectable({ providedIn: 'root' })
export class ReportsService {
  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      })
    };
  }

  getDeliveryReport(filters: {
    from?: string;
    to?: string;
    isPaid?: string;
    search?: string;
    riderName?: string;
    vehicleNumber?: string;
  }): Observable<any[]> {
    let params = new HttpParams().set('page', '1').set('pageSize', '500');
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.riderName) params = params.set('riderName', filters.riderName);
    if (filters.vehicleNumber) params = params.set('vehicleNumber', filters.vehicleNumber);
    if (filters.isPaid === 'paid') params = params.set('isPaid', 'true');
    if (filters.isPaid === 'unpaid') params = params.set('isPaid', 'false');

    return this.http
      .get<{ items: any[] }>(`${baseUrl}DeliveryOrders/filter`, { ...this.headers, params })
      .pipe(map(res => res?.items || []));
  }

  getMaintenanceReport(filters: { from?: string; to?: string; search?: string; vehicleId?: string }): Observable<any[]> {
    const query = filters.vehicleId ? `?vehicleId=${encodeURIComponent(filters.vehicleId)}` : '';
    return this.http.get<any[]>(`${baseUrl}Maintenance/records${query}`, this.headers).pipe(
      map((records) => {
        const from = filters.from ? new Date(filters.from) : null;
        const to = filters.to ? new Date(filters.to) : null;
        const search = (filters.search || '').toLowerCase().trim();

        return (records || []).filter((r) => {
          const date = r?.serviceDate ? new Date(r.serviceDate) : null;
          const inFrom = !from || (date && date >= from);
          const inTo = !to || (date && date <= new Date(to.getTime() + 86400000 - 1));
          const inSearch =
            !search ||
            (r.vehiclePlate || '').toLowerCase().includes(search) ||
            (r.description || '').toLowerCase().includes(search) ||
            (r.recordType || '').toLowerCase().includes(search);
          return inFrom && inTo && inSearch;
        });
      })
    );
  }

  getRiders(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}Riders`, this.headers);
  }

  getVehicles(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}Vehicles`, this.headers);
  }

  getSupplyReport(filters: {
    from?: string;
    to?: string;
    search?: string;
    productTypeId?: number | string | null;
    productItemId?: number | string | null;
  }): Observable<any[]> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.productTypeId) params = params.set('productTypeId', String(filters.productTypeId));
    if (filters.productItemId) params = params.set('productItemId', String(filters.productItemId));

    return this.http.get<any[]>(`${baseUrl}SupplyDispatch`, { ...this.headers, params });
  }

  getSupplyAnalytics(filters: {
    from?: string;
    to?: string;
    productTypeId?: number | string | null;
    productItemId?: number | string | null;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.productTypeId) params = params.set('productTypeId', String(filters.productTypeId));
    if (filters.productItemId) params = params.set('productItemId', String(filters.productItemId));

    return this.http.get<any>(`${baseUrl}SupplyDispatch/analytics`, { ...this.headers, params });
  }

  getSupplyProducts(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}SupplyProducts`, this.headers);
  }

  getStaffList(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}Staffs`, this.headers);
  }

  getProjectList(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}ProjectManagement/projects`, this.headers);
  }

  getProjectReport(filters: {
    from?: string;
    to?: string;
    search?: string;
    projectId?: number | string | null;
    staffId?: number | string | null;
    taskStatus?: string;
  }): Observable<any[]> {
    let params = new HttpParams();
    if (filters.from) params = params.set('from', filters.from);
    if (filters.to) params = params.set('to', filters.to);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.projectId) params = params.set('projectId', String(filters.projectId));
    if (filters.staffId) params = params.set('staffId', String(filters.staffId));
    if (filters.taskStatus && filters.taskStatus !== 'all') params = params.set('taskStatus', filters.taskStatus);

    return this.http.get<any[]>(`${baseUrl}ProjectManagement/report`, { ...this.headers, params });
  }

  getStaffReport(filters: {
    from?: string;
    to?: string;
    search?: string;
    staffId?: number | string | null;
    status?: string;
    requestType?: string;
  }): Observable<any[]> {
    let requestParams = new HttpParams();
    if (filters.status && filters.status !== 'all') requestParams = requestParams.set('status', filters.status);
    if (filters.requestType && filters.requestType !== 'all') requestParams = requestParams.set('requestType', filters.requestType);
    if (filters.staffId) requestParams = requestParams.set('staffId', String(filters.staffId));

    return forkJoin({
      staff: this.http.get<any[]>(`${baseUrl}Staffs`, this.headers),
      requests: this.http.get<any[]>(`${baseUrl}StaffRequests`, { ...this.headers, params: requestParams })
    }).pipe(
      map(({ staff, requests }) => {
        const staffRows = staff || [];
        const requestRows = requests || [];
        const from = filters.from ? new Date(filters.from) : null;
        const to = filters.to ? new Date(filters.to) : null;
        const search = (filters.search || '').toLowerCase().trim();
        const requestByStaff = new Map<number, any[]>();
        requestRows.forEach(r => {
          const bucket = requestByStaff.get(r.staffId) || [];
          bucket.push(r);
          requestByStaff.set(r.staffId, bucket);
        });

        const joined = staffRows.reduce((acc: any[], s: any) => {
          const reqs = requestByStaff.get(s.id) || [];
          if (!reqs.length) {
            acc.push({
              staffId: s.id,
              staffName: s.fullName,
              department: s.department,
              position: s.position,
              isActive: s.isActive,
              requestType: '-',
              status: '-',
              amount: null,
              title: '-',
              requestedAt: null,
              directedToStaffName: '-'
            });
            return acc;
          }

          reqs.forEach((r: any) => {
            acc.push({
              staffId: s.id,
              staffName: s.fullName,
              department: s.department,
              position: s.position,
              isActive: s.isActive,
              requestType: r.requestType,
              status: r.status,
              amount: r.amount,
              title: r.title,
              requestedAt: r.requestedAt,
              directedToStaffName: r.directedToStaffName || '-'
            });
          });
          return acc;
        }, []);

        return joined.filter((row: any) => {
          const rowDate = row.requestedAt ? new Date(row.requestedAt) : null;
          const inFrom = !from || (rowDate && rowDate >= from);
          const inTo = !to || (rowDate && rowDate <= new Date(to.getTime() + 86400000 - 1));
          const inSearch =
            !search ||
            (row.staffName || '').toLowerCase().includes(search) ||
            (row.department || '').toLowerCase().includes(search) ||
            (row.position || '').toLowerCase().includes(search) ||
            (row.requestType || '').toLowerCase().includes(search) ||
            (row.status || '').toLowerCase().includes(search) ||
            (row.title || '').toLowerCase().includes(search);
          return inFrom && inTo && inSearch;
        });
      })
    );
  }
}
